import React, {FC, useState, useEffect, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {KTCard, KTCardBody, KTSVG} from '../../../_metronic/helpers'
import {useAuth} from '../auth'
import {getSupplierDeliveryRecords} from './core/_requests'
import {SupplierDeliveryItem, PaginationState} from './core/SupplierDeliveryRecordModel'
import {SupplierDeliveryRecordRow} from './components/SupplierDeliveryRecordRow'
import {LoadingState, ErrorState, NoDataState} from '../../components/common/TableStates'
import {ImageModal} from '../../components/common/ImageModal'
import Paginator from '../common/Paginator' // Corrected Import Paginator path
import '../reports/payment-video.css'
import {TutorialVideoLink} from '../../tenant/TutorialVideoLink'
// Helper function to format date as YYYY-MM-DD
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const MainList: FC = () => {
  const {formatMessage, locale: language} = useIntl() // Get language from useIntl
  const {currentUser} = useAuth() // Get currentUser for token
  const [records, setRecords] = useState<SupplierDeliveryItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showImageModal, setShowImageModal] = useState(false)
  const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationState | null>(null) // Added pagination state

  const [searchParams, setSearchParams] = useSearchParams()
  const todayStr = useMemo(() => formatDate(new Date()), [])

  // Read filter values and page from searchParams
  const initialStartDate = searchParams.get('filter_date_start') || todayStr
  const initialEndDate = searchParams.get('filter_date_end') || todayStr
  const initialBarcode = searchParams.get('filter_barcode') || ''
  const initialModel = searchParams.get('filter_model') || ''
  const initialPage = parseInt(searchParams.get('current_page') || '1', 10)

  // Local state for input fields, synced with searchParams
  const [startDateInput, setStartDateInput] = useState<string>(initialStartDate)
  const [endDateInput, setEndDateInput] = useState<string>(initialEndDate)
  const [barcodeInput, setBarcodeInput] = useState<string>(initialBarcode)
  const [modelInput, setModelInput] = useState<string>(initialModel)
  const [showVideoHighlight, setShowVideoHighlight] = useState(false)

  useEffect(() => {
    const hasSeenVideo = document.cookie
      .split(';')
      .some(item => item.trim().startsWith('supplier_delivery_video='));

    setShowVideoHighlight(!hasSeenVideo);
  }, []);

  // Fetch records when searchParams change (filters or page)
  useEffect(() => {
    const currentPage = parseInt(searchParams.get('current_page') || '1', 10)
    const currentStartDate = searchParams.get('filter_date_start') || todayStr
    const currentEndDate = searchParams.get('filter_date_end') || todayStr
    const currentBarcode = searchParams.get('filter_barcode') || ''
    const currentModel = searchParams.get('filter_model') || ''

    // Update input fields if searchParams change externally
    setStartDateInput(currentStartDate)
    setEndDateInput(currentEndDate)
    setBarcodeInput(currentBarcode)
    setModelInput(currentModel)

    fetchRecords(currentStartDate, currentEndDate, currentBarcode, currentModel, currentPage)
  }, [searchParams, todayStr]) // Re-fetch if searchParams or todayStr change

  const handleFilter = () => {
    const trimmedBarcode = barcodeInput.trim()
    const trimmedModel = modelInput.trim()

    searchParams.set('filter_date_start', startDateInput)
    searchParams.set('filter_date_end', endDateInput)

    if (trimmedBarcode) {
      searchParams.set('filter_barcode', trimmedBarcode)
    } else {
      searchParams.delete('filter_barcode')
    }
    if (trimmedModel) {
      searchParams.set('filter_model', trimmedModel)
    } else {
      searchParams.delete('filter_model')
    }
    searchParams.set('current_page', '1')
    setSearchParams(searchParams, {replace: true})
  }

  const clearFilters = () => {
    setStartDateInput(todayStr)
    setEndDateInput(todayStr)
    setBarcodeInput('')
    setModelInput('')
    searchParams.delete('filter_date_start')
    searchParams.delete('filter_date_end')
    searchParams.delete('filter_barcode')
    searchParams.delete('filter_model')
    searchParams.set('current_page', '1')
    setSearchParams(searchParams, {replace: true})
  }

  // handlePageChange is no longer needed as Paginator handles searchParams internally
  // const handlePageChange = (newPage: number) => {
  //   searchParams.set('current_page', newPage.toString()) // use current_page
  //   setSearchParams(searchParams, {replace: true})
  // }

  // Handlers for image modal
  const handleImageClick = (imageUrl: string) => {
    setEnlargedImageUrl(imageUrl)
    setShowImageModal(true)
  }

  const handleCloseImageModal = () => {
    setShowImageModal(false)
    setEnlargedImageUrl(null)
  }

  const fetchRecords = async (
    start: string,
    end: string,
    barcode: string,
    model: string,
    page: number
  ) => {
    setIsLoading(true)
    setError(null)
    setPagination(null) // Reset pagination on new fetch

    const filterParams = new URLSearchParams()
    if (start) filterParams.set('filter_date_start', start)
    if (end) filterParams.set('filter_date_end', end)
    if (barcode) filterParams.set('filter_barcode', barcode) // API param name might differ
    if (model) filterParams.set('filter_model', model) // API param name might differ
    filterParams.set('page', page.toString())

    const filtersObj = filterParams.toString()

    try {
      const response = await getSupplierDeliveryRecords(filtersObj, currentUser?.token, language)
      if (response && response.data) {
        setRecords(response.data)
        if (response.pagination) {
          setPagination(response.pagination) // Set pagination state
        } else {
          // If API doesn't return pagination, create a default for single page
          setPagination({
            total: response.data.length,
            per_page: response.data.length || 15, // assume all items if not specified
            current_page: 1,
            last_page: 1,
            from: 1,
            to: response.data.length,
          })
        }
      } else {
        setRecords([])
        setPagination(null)
      }
      setIsLoading(false)
    } catch (err) {
      console.error(err)
      setError(formatMessage({id: 'something_wrong_desc'}))
      setIsLoading(false)
    }
  }

  const renderFilters = () => {
    // No need to check language here, flexbox handles LTR/RTL alignment
    return (
    <div className='card-body border-bottom pb-10 mb-5'>
      <div className='row gy-3'>
        {/* Barcode Input */}
        <div className='col-sm-12 col-md-3'>
          <label className='form-label fs-6 fw-bold'>{formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.ITEM_CODE'})}:</label>
          <input
            type='text'
            className='form-control form-control-solid'
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            placeholder={formatMessage({id: 'SUPPLIER_DELIVERY.FILTER.BARCODE_PLACEHOLDER'})}
          />
        </div>

        {/* Model Input */}
        <div className='col-sm-12 col-md-3'>
          <label className='form-label fs-6 fw-bold'>{formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.MODEL'})}:</label>
          <input
            type='text'
            className='form-control form-control-solid'
            value={modelInput}
            onChange={(e) => setModelInput(e.target.value)}
            placeholder={formatMessage({id: 'SUPPLIER_DELIVERY.FILTER.MODEL_PLACEHOLDER'})}
          />
        </div>
        
        {/* Date Start Input */}
        <div className='col-sm-12 col-md-3'>
          <label className='form-label fs-6 fw-bold'>{formatMessage({id: 'date_start'})}:</label>
          <input
            type='date'
            className='form-control form-control-solid'
            value={startDateInput}
            onChange={(e) => setStartDateInput(e.target.value)}
          />
        </div>

        {/* Date End Input */}
        <div className='col-sm-12 col-md-3'>
          <label className='form-label fs-6 fw-bold'>{formatMessage({id: 'date_end'})}:</label>
          <input
            type='date'
            className='form-control form-control-solid'
            value={endDateInput}
            onChange={(e) => setEndDateInput(e.target.value)}
          />
        </div>
      </div>
      {/* Buttons row - Use flexbox for alignment */}
      <div className='row mt-5 d-flex justify-content-end'>
         {/* Buttons will align right in LTR, left in RTL */}
         {/* Order in code: Filter then Reset */}
         <div className='col-sm-6 col-md-3'> {/* Filter Button */}
              <button
                type='button'
                className='btn btn-dark w-100'
                onClick={handleFilter}
                disabled={isLoading}
              >
                {formatMessage({id: 'filter'})}
              </button>
          </div>
          <div className='col-sm-6 col-md-3'> {/* Reset Button */}
              <button
                type='button'
                className='btn btn-light w-100'
                onClick={clearFilters}
                disabled={isLoading}
              >
                {formatMessage({id: 'reset'})}
              </button>
          </div>
      </div>
    </div>
   );
  };
  const renderTableAndPagination = () => {
    if (isLoading) {
      // Wrap LoadingState in KTCardBody for consistent padding if needed, or return as is
      return <KTCardBody className='py-4 d-flex justify-content-center align-items-center'><LoadingState /></KTCardBody>;
    }
  
    if (error) {
      return <KTCardBody className='py-4'><ErrorState message={error} /></KTCardBody>;
    }
    
    if (records.length === 0) {
      // Wrap NoDataState for consistent padding
      return <KTCardBody className='py-4'><NoDataState /></KTCardBody>;
    }

    return (
      <KTCardBody className='py-4'>
        <div> {/* Removed 'table-responsive' class */}
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th className='min-w-120px'>{formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.IMAGE'})}</th> {/* Increased min-width for image */}
                <th className='min-w-150px'>{formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.ITEM_CODE'})}</th>
                <th className='min-w-150px'>{formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.MODEL'})}</th> {/* Added Model Header */}
                <th className='min-w-200px'>{formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.ITEM_NAME'})}</th>
                <th className='min-w-120px'>{formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.DELIVERY_DATE'})}</th>
                <th className='min-w-100px'>{formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.QUANTITY_DELIVERED'})}</th>
                <th className='min-w-100px'>{formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.CURRENT_FC_STOCK'})}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((item, idx) => (
                <SupplierDeliveryRecordRow
                  key={`${item.barcode}-${item.po_id}-${idx}`}
                  item={item}
                  onImageClick={handleImageClick} // Pass handler down
                />
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination Section */}
        {pagination && pagination.total > 0 && pagination.last_page > 1 && (
           <div className='d-flex justify-content-center mt-5'>
            <Paginator
              totalResult={pagination.total}
              perPage={pagination.per_page}
              // currentPage is handled by Paginator via searchParams.get('current_page')
            />
          </div>
        )}
      </KTCardBody>
    );
  }

  return (
    <KTCard>
      <div className='d-flex justify-content-end p-5 pb-0'>
        <TutorialVideoLink
          section="delivery_record"
          fallback="https://www.youtube.com/watch?v=BhDCNfFtcbQ"
          target="_blank"
          rel="noopener noreferrer"
          className={`text-primary d-flex align-items-center ${
            showVideoHighlight ? 'video-highlight' : ''
          }`}
          onClick={() => {
            document.cookie = "supplier_delivery_video=seen";
            setShowVideoHighlight(false);
          }}
        >
          <i className='fab fa-youtube text-danger fs-2'></i>
          <span className='ms-2'>
          {formatMessage({ id: 'video_link' })}
        </span>
        </TutorialVideoLink>
      </div>

      {renderFilters()}
      {renderTableAndPagination()}
      {/* Use Common Image Modal */}
      <ImageModal
        show={showImageModal}
        imageUrl={enlargedImageUrl}
        onClose={handleCloseImageModal}
        altText={formatMessage({id: 'SUPPLIER_DELIVERY.TABLE.ITEM_NAME'})} // Or a more generic alt text
      />
    </KTCard>
  )
}


const SupplierDeliveryRecordList: FC = () => {
    return <MainList />
}

export {SupplierDeliveryRecordList}
